
import { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getPointsHistory, PointsData, PointsResponse } from '@/services/databaseService';
import { format } from 'date-fns';

interface PointsHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

const PointsHistoryModal = ({ open, onOpenChange, userId }: PointsHistoryModalProps) => {
  const [pointsHistory, setPointsHistory] = useState<PointsData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadPointsHistory();
    }
  }, [open, userId]);

  const loadPointsHistory = async () => {
    setLoading(true);
    const response = await getPointsHistory(userId);
    setLoading(false);
    
    if (response.success && response.data) {
      setPointsHistory(response.data);
      setError(null);
    } else {
      setError(response.error?.toString() || "Failed to load points history");
      setPointsHistory([]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-xl mb-4">Points History</DialogTitle>
        </DialogHeader>
        
        {loading && (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-700"></div>
          </div>
        )}
        
        {error && (
          <div className="text-red-500 p-4 text-center">
            {error}
          </div>
        )}
        
        {!loading && !error && pointsHistory.length === 0 && (
          <div className="text-center py-8">
            No points history found.
          </div>
        )}
        
        {!loading && pointsHistory.length > 0 && (
          <div className="overflow-y-auto max-h-[400px]">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="p-2 border">Date</th>
                  <th className="p-2 border">Transaction ID</th>
                  <th className="p-2 border">Points</th>
                </tr>
              </thead>
              <tbody>
                {pointsHistory.map((item) => (
                  <tr key={item.id} className="border-t hover:bg-gray-50">
                    <td className="p-2 border">{format(new Date(item.created_at), 'dd MMM yyyy')}</td>
                    <td className="p-2 border">{item.transaction_id}</td>
                    <td className="p-2 border font-medium text-green-600">+{item.points_amt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PointsHistoryModal;
